/**
 * `ServerErrorScreen` — GVW-164 standalone state page.
 *
 * 500 server error page. Reads ?correlation= query param. Live region role='alert'.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-164/brief.md
 * Copy: error-catalog.md GV-ERR-500, copy-deck.md gvw.164.*
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function ServerErrorScreen() {
  const liveRegion = useLiveRegion();
  const [searchParams] = useSearchParams();
  
  // Query param per brief.md step 4.f
  const correlationId = searchParams.get('correlation') || 'N/A';

  useEffect(() => {
    // Copy key: gvw.164.title
    document.title = 'Server error · GeoViewer';
    // Alert role per error-catalog.md GV-ERR-500
    liveRegion.announce('alert', 'Server error');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="server-error-heading"
      data-gvw-id="GVW-164"
      data-route="/500"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.danger}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.semantic.danger,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          error
        </div>

        {/* Copy key: gvw.164.title from copy-deck.md */}
        <h1
          id="server-error-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Server error
        </h1>

        {/* Copy key: gvw.164.body with correlation substitution */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '50ch',
          }}
        >
          Something failed on our side. Try again, or check the status page.
        </p>

        {/* Recovery actions per navigation-map.md §8 */}
        <div
          style={{
            display: 'flex',
            gap: tokens.space[3],
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <button
            type="button"
            onClick={() => {
              // Refresh page to retry
              window.location.reload();
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
              background: tokens.color.accent.primary,
              color: tokens.color.accent.on,
              border: 'none',
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 18,
              }}
            >
              refresh
            </span>
            Try again
          </button>

          <button
            type="button"
            onClick={() => {
              window.open('/status', '_blank');
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
              background: tokens.color.surface[2],
              color: tokens.color.ink.primary,
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              fontSize: tokens.font.size.md,
              fontWeight: tokens.font.weight.medium,
              fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                fontFamily: 'Material Symbols Outlined',
                fontSize: 18,
              }}
            >
              monitor_heart
            </span>
            Open status page
          </button>
        </div>

        {/* Correlation ID per brief.md §8 + accessibility plan */}
        <div
          style={{
            marginTop: tokens.space[2],
            fontSize: tokens.font.size.sm,
            color: tokens.color.ink.muted,
          }}
        >
          <span>Reference: </span>
          <code
            style={{
              fontFamily: tokens.font.family.mono,
              background: tokens.color.surface[2],
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              borderRadius: tokens.radius.sm,
              userSelect: 'all',
            }}
          >
            {correlationId}
          </code>
          <button
            type="button"
            onClick={() => {
              navigator.clipboard.writeText(correlationId);
            }}
            aria-label="Copy correlation id"
            style={{
              marginLeft: tokens.space[2],
              background: 'transparent',
              border: 'none',
              color: tokens.color.accent.primary,
              cursor: 'pointer',
              fontSize: tokens.font.size.sm,
            }}
          >
            Copy
          </button>
        </div>
      </section>
    </main>
  );
}